import React, { useState } from 'react';
import ExpansionPanel from '@material-ui/core/Accordion';
import ExpansionPanelDetails from '@material-ui/core/AccordionDetails';
import ExpansionPanelSummary from '@material-ui/core/AccordionSummary';
import { withStyles } from '@material-ui/core/styles';

import { colors } from 'src/themes/colors';
import { ReactComponent as IconArrow } from 'src/assets/svgs/icon-arrow-down.svg';

import MemberSettingsPanel from './MemberProfilePanel';

const Accordion = withStyles({
  root: {
    border: '1px solid #E9EEF4',
    borderRadius: 5,
    boxShadow: 'none',
    marginBottom: 10,
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
      marginBottom: 10,
    },
  },
  expanded: {},
})(ExpansionPanel);

const AccordionSummary = withStyles({
  root: {
    paddingLeft: 16,
    paddingRight: 16,
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
    },
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
})(ExpansionPanelSummary);

const AccordionDetails = withStyles({
  root: {
    flexDirection: 'column',
    padding: 16,
    paddingTop: 0,
  },
})(ExpansionPanelDetails);

export interface AdditionalBenefit {
  id: string;
  title: string;
  description: string;
  link: string;
}

interface Props {
  companyName: string;
  firstName?: string | null;
  benefits: AdditionalBenefit[];
}

export enum AdditionalBenefitsSectionTestID {
  BenefitActive = 'AdditionalBenefitsSection.BenefitActive',
  BenefitNotActive = 'AdditionalBenefitsSection.BenefitNotActive',
}

export const AdditionalBenefitsSection: React.FC<Props> = ({
  companyName,
  firstName,
  benefits,
}) => {
  const [expanded, setExpanded] = useState(null);

  const handleChange = (id) => (_, newExpanded) => {
    setExpanded(newExpanded ? id : null);
  };

  if (!benefits?.length) {
    return null;
  }

  return (
    <MemberSettingsPanel title="Additional Benefits">
      <div>
        <h4 className="font-normal mb-3 leading-6" style={{ color: colors.black2 }}>
          Below are additional benefits that {companyName} offers outside of Modern Health that{' '}
          {firstName} can use
        </h4>

        {benefits.map((benefit) => {
          const active = expanded === benefit.id;

          return (
            <Accordion
              expanded={active}
              onChange={handleChange(benefit.id)}
              key={benefit.id}
              data-testid={
                active
                  ? AdditionalBenefitsSectionTestID.BenefitActive
                  : AdditionalBenefitsSectionTestID.BenefitNotActive
              }
            >
              <AccordionSummary>
                <p className="text-gray-850 font-black">{benefit.title}</p>
                <IconArrow fill={colors.purple1} className={active ? 'transform rotate-180' : ''} />
              </AccordionSummary>
              <AccordionDetails>
                <p className="font-normal leading-6" style={{ color: colors.black2 }}>
                  {benefit.description}
                </p>
                <button
                  className="flex w-full font-extrabold justify-center text-white p-2 mt-4 rounded whitespace-no-wrap bg-blue-400"
                  onClick={() => window.open(benefit.link, '_blank')}
                >
                  Go to website
                </button>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </div>
    </MemberSettingsPanel>
  );
};
